import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Radio, Users, TrendingUp, Clock, MapPin, Trophy, ChevronDown, ChevronUp, RefreshCw, Video, ExternalLink, Copy, Check, Star, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import axios from 'axios';
import { API } from '@/App';
import { format, parseISO, formatDistanceToNow } from 'date-fns';

// Helper to extract YouTube video ID from various URL formats
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/live\/|youtube\.com\/embed\/)([^&\?\/]+)/,
    /^([a-zA-Z0-9_-]{11})$/  // Direct video ID
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// YouTube Embed Component
const YouTubeLiveEmbed = ({ url, isLive }) => {
  const videoId = getYouTubeVideoId(url);
  
  if (!videoId) {
    return (
      <div className="bg-gray-900 rounded-xl aspect-video flex items-center justify-center">
        <div className="text-center text-gray-400">
          <Video className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Invalid video URL</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative rounded-xl overflow-hidden shadow-lg">
      {isLive && (
        <div className="absolute top-3 left-3 z-10 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
          <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
          LIVE
        </div>
      )}
      <div className="aspect-video">
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&rel=0`}
          title="Live Match Stream"
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
};

// OBS Overlay Link Component
const OverlayLinkCard = ({ matchId }) => {
  const [copied, setCopied] = useState(false);
  const overlayUrl = `${window.location.origin}/overlay/${matchId}`;
  
  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
            <Video className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-purple-900 text-sm">OBS Score Overlay</h4>
            <p className="text-xs text-purple-700/70 mt-0.5">Add live score to your stream</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                onClick={() => copyToClipboard(`${overlayUrl}?theme=detailed`)}
                className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded text-xs font-medium text-purple-700 hover:bg-purple-100 transition-colors border border-purple-200"
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                Detailed
              </button>
              <button
                onClick={() => copyToClipboard(`${overlayUrl}?theme=ipl`)}
                className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded text-xs font-medium text-purple-700 hover:bg-purple-100 transition-colors border border-purple-200"
              >
                <Copy className="w-3 h-3" />
                IPL Style
              </button>
              <button
                onClick={() => copyToClipboard(`${overlayUrl}?theme=minimal`)}
                className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded text-xs font-medium text-purple-700 hover:bg-purple-100 transition-colors border border-purple-200"
              >
                <Copy className="w-3 h-3" />
                Minimal
              </button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function LiveMatchView() {
  const { matchId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeInnings, setActiveInnings] = useState(1);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [highlights, setHighlights] = useState([]);
  const [activeTab, setActiveTab] = useState('scorecard'); // 'scorecard' or 'highlights'

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/matches/${matchId}/live-stats`);
      setData(response.data);
      if (response.data.current_innings) {
        setActiveInnings(response.data.current_innings);
      }
      
      // Fetch highlights
      try {
        const highlightsRes = await axios.get(`${API}/matches/${matchId}/highlights`);
        setHighlights(highlightsRes.data.highlights || []);
      } catch (e) {
        console.error('Error fetching highlights:', e);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto refresh every 10 seconds for live matches
    const interval = setInterval(() => {
      if (autoRefresh) fetchData();
    }, 10000);
    return () => clearInterval(interval);
  }, [matchId, autoRefresh]);

  const formatOvers = (overs) => {
    const fullOvers = Math.floor(overs || 0);
    const balls = Math.round(((overs || 0) - fullOvers) * 10);
    return `${fullOvers}.${balls}`;
  };

  const getBallColor = (ball) => {
    if (ball?.is_wicket) return 'bg-red-500 text-white';
    if (ball?.runs === 6) return 'bg-green-500 text-white';
    if (ball?.runs === 4) return 'bg-blue-500 text-white';
    if (ball?.is_extra && ball?.extra_type === 'wide') return 'bg-yellow-500 text-white';
    if (ball?.is_extra && ball?.extra_type === 'no_ball') return 'bg-orange-500 text-white';
    if (ball?.is_extra) return 'bg-amber-400 text-white';
    if (ball?.runs === 0) return 'bg-gray-300 text-gray-600';
    return 'bg-blue-400 text-white';
  };

  const getBallLabel = (ball) => {
    if (ball?.is_wicket) return 'W';
    if (ball?.is_extra && ball?.extra_type === 'wide') return 'Wd';
    if (ball?.is_extra && ball?.extra_type === 'no_ball') return 'Nb';
    if (ball?.is_extra) return 'B';
    return ball?.runs ?? '-';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="text-center">
          <h2 className="text-xl font-bold">Match not found</h2>
          <Link to="/live"><Button className="mt-4">Back to Live Matches</Button></Link>
        </div>
      </div>
    );
  }

  const currentInningsData = data.innings?.find(i => i.innings_number === activeInnings) || data.innings?.[0];
  const isLive = data.match?.status === 'live';

  return (
    <div className="min-h-screen bg-stone-100 pb-20">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/live" className="text-gray-600">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="text-center flex-1">
            <h1 className="font-semibold text-gray-800">{data.league?.name || 'Cricket Match'}</h1>
            <p className="text-xs text-gray-500">{data.match?.ground}</p>
          </div>
          <button onClick={() => setAutoRefresh(!autoRefresh)} className={`p-2 rounded-full ${autoRefresh ? 'bg-green-100' : 'bg-gray-100'}`}>
            <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'text-green-600 animate-spin' : 'text-gray-400'}`} style={{animationDuration: '3s'}} />
          </button>
        </div>
      </header>

      {/* YouTube Live Stream (if available) */}
      {data.match?.live_stream_url && (
        <div className="max-w-4xl mx-auto px-4 pt-4">
          <YouTubeLiveEmbed url={data.match.live_stream_url} isLive={isLive} />
        </div>
      )}

      {/* OBS Overlay Link for Streamers */}
      {isLive && (
        <div className="max-w-4xl mx-auto px-4 pt-4">
          <OverlayLinkCard matchId={matchId} />
        </div>
      )}

      {/* Match Summary Card */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <Card className="overflow-hidden">
          {/* Status Bar */}
          <div className={`px-4 py-2 ${isLive ? 'bg-red-500' : 'bg-gray-500'} text-white flex items-center justify-between`}>
            <div className="flex items-center gap-2">
              {isLive && <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>}
              <span className="font-medium">{isLive ? 'LIVE' : data.status}</span>
            </div>
            <span className="text-sm">{data.match?.overs} Overs</span>
          </div>

          <CardContent className="p-4">
            {/* Teams and Scores */}
            <div className="space-y-4">
              {/* Team A */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold">
                    {data.team_a?.short_name?.charAt(0) || 'A'}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">{data.team_a?.name}</div>
                    <div className="text-xs text-gray-500">{data.team_a?.short_name}</div>
                  </div>
                </div>
                <div className="text-right">
                  {data.innings?.find(i => i.batting_team?.team_id === data.team_a?.team_id) ? (
                    <>
                      <div className="text-2xl font-bold text-gray-800">
                        {data.innings.find(i => i.batting_team?.team_id === data.team_a?.team_id)?.total_runs || 0}
                        <span className="text-gray-400">/{data.innings.find(i => i.batting_team?.team_id === data.team_a?.team_id)?.total_wickets || 0}</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        ({formatOvers(data.innings.find(i => i.batting_team?.team_id === data.team_a?.team_id)?.total_overs)} ov)
                      </div>
                    </>
                  ) : (
                    <span className="text-gray-400">Yet to bat</span>
                  )}
                </div>
              </div>

              {/* Team B */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-bold">
                    {data.team_b?.short_name?.charAt(0) || 'B'}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">{data.team_b?.name}</div>
                    <div className="text-xs text-gray-500">{data.team_b?.short_name}</div>
                  </div>
                </div>
                <div className="text-right">
                  {data.innings?.find(i => i.batting_team?.team_id === data.team_b?.team_id) ? (
                    <>
                      <div className="text-2xl font-bold text-gray-800">
                        {data.innings.find(i => i.batting_team?.team_id === data.team_b?.team_id)?.total_runs || 0}
                        <span className="text-gray-400">/{data.innings.find(i => i.batting_team?.team_id === data.team_b?.team_id)?.total_wickets || 0}</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        ({formatOvers(data.innings.find(i => i.batting_team?.team_id === data.team_b?.team_id)?.total_overs)} ov)
                      </div>
                    </>
                  ) : (
                    <span className="text-gray-400">Yet to bat</span>
                  )}
                </div>
              </div>
            </div>

            {/* Match Result or Status */}
            {data.result ? (
              <div className="mt-4 p-3 bg-green-50 rounded-lg text-center">
                <p className="font-semibold text-green-700">{data.result}</p>
              </div>
            ) : currentInningsData?.runs_needed && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700 text-center">
                  <span className="font-bold">{currentInningsData.batting_team?.short_name}</span> need 
                  <span className="font-bold"> {currentInningsData.runs_needed} runs</span> from 
                  <span className="font-bold"> {currentInningsData.balls_remaining} balls</span>
                  <span className="text-gray-500"> (RRR: {currentInningsData.required_rr})</span>
                </p>
              </div>
            )}

            {/* Toss Info */}
            {data.toss?.winner && (
              <div className="mt-3 text-xs text-gray-500 text-center">
                Toss: {data.toss.winner.name} won and chose to {data.toss.decision}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Current Batters & Bowler */}
        {isLive && currentInningsData && (
          <Card className="mt-4">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Current Batters */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Batting</h3>
                  {currentInningsData.batters?.filter(b => b.is_striker || b.is_non_striker).map(batter => (
                    <div key={batter.player_id} className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${batter.is_striker ? 'text-blue-600' : 'text-gray-700'}`}>
                          {batter.name.split(' ').slice(-1)[0]}
                          {batter.is_striker && ' *'}
                        </span>
                      </div>
                      <span className="font-bold">{batter.runs}<span className="text-gray-400 text-sm">({batter.balls})</span></span>
                    </div>
                  ))}
                </div>
                
                {/* Current Bowler */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Bowling</h3>
                  {currentInningsData.bowlers?.filter(b => b.is_current).map(bowler => (
                    <div key={bowler.player_id} className="flex items-center justify-between py-1">
                      <span className="font-medium text-gray-700">{bowler.name.split(' ').slice(-1)[0]}</span>
                      <span className="font-bold">{bowler.wickets}-{bowler.runs}<span className="text-gray-400 text-sm">({bowler.overs})</span></span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Partnership */}
              {currentInningsData.current_partnership?.runs > 0 && (
                <div className="mt-3 pt-3 border-t text-center text-sm text-gray-600">
                  Partnership: <span className="font-semibold">{currentInningsData.current_partnership.runs}</span> ({currentInningsData.current_partnership.balls} balls)
                </div>
              )}

              {/* This Over */}
              {currentInningsData.recent_overs?.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <div className="text-xs text-gray-500 mb-2">This Over:</div>
                  <div className="flex gap-1 flex-wrap">
                    {currentInningsData.recent_overs[currentInningsData.recent_overs.length - 1]?.balls?.map((ball, i) => (
                      <div key={i} className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${getBallColor(ball)}`}>
                        {getBallLabel(ball)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Innings Tabs */}
        {data.innings?.length > 0 && (
          <Tabs value={String(activeInnings)} onValueChange={(v) => setActiveInnings(Number(v))} className="mt-4">
            <TabsList className="grid w-full grid-cols-2 bg-white rounded-xl p-1">
              {data.innings.map(inn => (
                <TabsTrigger 
                  key={inn.innings_number} 
                  value={String(inn.innings_number)}
                  className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                >
                  {inn.batting_team?.short_name} Innings
                </TabsTrigger>
              ))}
            </TabsList>

            {data.innings.map(inn => (
              <TabsContent key={inn.innings_number} value={String(inn.innings_number)} className="mt-4 space-y-4">
                {/* Batting Card */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center justify-between">
                      <span>🏏 {inn.batting_team?.name} Batting</span>
                      <span className="text-2xl font-bold">{inn.total_runs}/{inn.total_wickets}</span>
                    </CardTitle>
                    <div className="text-sm text-gray-500">
                      {formatOvers(inn.total_overs)} overs • RR: {inn.current_rr}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="text-gray-500 text-xs border-b">
                          <tr>
                            <th className="text-left py-2">Batter</th>
                            <th className="text-center py-2">R</th>
                            <th className="text-center py-2">B</th>
                            <th className="text-center py-2">4s</th>
                            <th className="text-center py-2">6s</th>
                            <th className="text-center py-2">SR</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inn.batters?.map(batter => (
                            <tr key={batter.player_id} className={`border-b ${batter.is_striker ? 'bg-blue-50' : batter.is_non_striker ? 'bg-gray-50' : ''}`}>
                              <td className="py-2">
                                <div className="flex items-center gap-2">
                                  <span className={`font-medium ${batter.out ? 'text-gray-500' : 'text-gray-800'}`}>
                                    {batter.name.split(' ').slice(-2).join(' ')}
                                    {batter.is_striker && <span className="text-blue-600"> *</span>}
                                  </span>
                                  {batter.out && <span className="text-xs text-red-500">({batter.dismissal})</span>}
                                </div>
                              </td>
                              <td className="text-center font-bold">{batter.runs}</td>
                              <td className="text-center text-gray-600">{batter.balls}</td>
                              <td className="text-center text-blue-600">{batter.fours}</td>
                              <td className="text-center text-green-600">{batter.sixes}</td>
                              <td className="text-center text-gray-600">{batter.strike_rate}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Extras */}
                    <div className="mt-3 pt-3 border-t text-sm text-gray-600">
                      <span className="font-medium">Extras: </span>
                      {inn.extras?.wides || 0}w, {inn.extras?.no_balls || 0}nb, {inn.extras?.byes || 0}b, {inn.extras?.leg_byes || 0}lb
                      <span className="font-semibold ml-2">
                        ({(inn.extras?.wides || 0) + (inn.extras?.no_balls || 0) + (inn.extras?.byes || 0) + (inn.extras?.leg_byes || 0)})
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Bowling Card */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">⚾ {inn.bowling_team?.name} Bowling</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="text-gray-500 text-xs border-b">
                          <tr>
                            <th className="text-left py-2">Bowler</th>
                            <th className="text-center py-2">O</th>
                            <th className="text-center py-2">M</th>
                            <th className="text-center py-2">R</th>
                            <th className="text-center py-2">W</th>
                            <th className="text-center py-2">Econ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inn.bowlers?.map(bowler => (
                            <tr key={bowler.player_id} className={`border-b ${bowler.is_current ? 'bg-orange-50' : ''}`}>
                              <td className="py-2">
                                <span className="font-medium text-gray-800">
                                  {bowler.name.split(' ').slice(-2).join(' ')}
                                  {bowler.is_current && <span className="text-orange-600"> *</span>}
                                </span>
                              </td>
                              <td className="text-center">{bowler.overs}</td>
                              <td className="text-center text-gray-500">{bowler.maidens}</td>
                              <td className="text-center">{bowler.runs}</td>
                              <td className="text-center font-bold text-green-600">{bowler.wickets}</td>
                              <td className="text-center text-gray-600">{bowler.economy}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Fall of Wickets */}
                {inn.fall_of_wickets?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">Fall of Wickets</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {inn.fall_of_wickets.map((fow, i) => (
                          <Badge key={i} variant="outline" className="text-sm">
                            {fow.score}/{fow.wicket} ({fow.player.split(' ').slice(-1)[0]}, {fow.overs} ov)
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Recent Overs */}
                {inn.recent_overs?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">Recent Overs</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {inn.recent_overs.slice().reverse().map((over, i) => (
                          <div key={i} className="flex items-center gap-3 py-2 border-b last:border-0">
                            <span className="text-sm text-gray-500 w-16">Over {over.over}</span>
                            <div className="flex gap-1 flex-1 flex-wrap">
                              {over.balls?.map((ball, j) => (
                                <div key={j} className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${getBallColor(ball)}`}>
                                  {getBallLabel(ball)}
                                </div>
                              ))}
                            </div>
                            <span className="font-semibold text-gray-700">{over.runs} runs</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            ))}
          </Tabs>
        )}

        {/* Highlights Section */}
        <Card className="mt-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Match Highlights
              {highlights.length > 0 && (
                <Badge className="bg-amber-100 text-amber-700 border-none ml-2">
                  {highlights.length}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {highlights.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Star className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No highlights yet</p>
                <p className="text-xs text-gray-400 mt-1">Key moments will appear here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {highlights.map((highlight, index) => (
                  <div 
                    key={highlight.highlight_id || index}
                    className="flex items-start gap-3 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-100"
                  >
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      {highlight.event_type === 'six' && <span className="text-xl">🔥</span>}
                      {highlight.event_type === 'four' && <span className="text-xl">4️⃣</span>}
                      {highlight.event_type === 'wicket' && <span className="text-xl">🎯</span>}
                      {highlight.event_type === 'catch' && <span className="text-xl">🙌</span>}
                      {highlight.event_type === 'run_out' && <span className="text-xl">⚡</span>}
                      {highlight.event_type === 'stumping' && <span className="text-xl">🧤</span>}
                      {!['six', 'four', 'wicket', 'catch', 'run_out', 'stumping'].includes(highlight.event_type) && (
                        <Star className="w-5 h-5 text-amber-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-800 capitalize">
                        {highlight.event_type.replace('_', ' ')}
                      </div>
                      {highlight.player_name && (
                        <div className="text-sm text-gray-600">{highlight.player_name}</div>
                      )}
                      {highlight.description && (
                        <div className="text-xs text-gray-500 mt-1">{highlight.description}</div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      {highlight.over_number && (
                        <div className="text-xs font-medium text-amber-600">
                          Over {highlight.over_number}
                        </div>
                      )}
                      {highlight.created_at && (
                        <div className="text-xs text-gray-400">
                          {formatDistanceToNow(new Date(highlight.created_at), { addSuffix: true })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
